/**
 * ResourceCost — stardust and candy drawn as the resources they are
 * (2026-08-30 ask: "dust and candy more prominently displayed with their
 * in-game graphic logo").
 *
 * Every icon here is our own drawing, not a game asset: a dust mound with
 * sparkles for stardust, a wrapped candy for candy (and the same candy wearing
 * an XL tag), and a faceted medallion for Mega Energy. The game's own vial and
 * medallion PNGs were removed on 2026-09-10 (docs/ip-audit.md). Colour comes from the --stardust / --candy
 * tokens so both themes read them the way the game does (violet-magenta
 * dust, warm amber candy) without touching the reserved --bonus pink.
 *
 * <PowerUpCostRow> is the shared "what this path costs" line: one chip per
 * resource, icon + bold tabular number, only for resources actually owed.
 * Used by the detail page's PvP target and the Stardust Planner rows, so
 * the two surfaces cannot drift.
 */
import type { CSSProperties } from "react";
import { formatDust, type PowerUpCost } from "@/lib/powerUpCosts";

function iconSize(px: number): CSSProperties {
  return { width: px, height: px, flexShrink: 0 };
}

/**
 * The Stardust mark: our own drawing, a violet mound of dust with four-point
 * sparkles rising off it (2026-09-10). It replaced a bundled PNG of the
 * game's own Stardust vial, which was a Scopely-owned asset (docs/ip-audit.md).
 * Colour from --stardust; `size` is the box.
 */
export function StardustIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={{ ...iconSize(size), color: "hsl(var(--stardust))" }}
      aria-hidden
      data-testid="stardust-icon"
    >
      {/* mound */}
      <path
        d="M3.5 19.5c1.5-4.5 5-7 8.5-7s7 2.5 8.5 7c-2.5 1-5.5 1.5-8.5 1.5s-6-.5-8.5-1.5z"
        fill="currentColor"
      />
      <path
        d="M7 17.2c1.5-1.6 3.2-2.4 5-2.4s3.5.8 5 2.4"
        fill="none"
        stroke="hsl(var(--stardust) / 0.35)"
        strokeWidth="1"
        strokeLinecap="round"
      />
      {/* sparkles */}
      <path d="M12 2.5c.5 2.6 1.6 3.7 4.2 4.2-2.6.5-3.7 1.6-4.2 4.2-.5-2.6-1.6-3.7-4.2-4.2 2.6-.5 3.7-1.6 4.2-4.2z" fill="currentColor" />
      <path d="M5 8.5c.3 1.3.8 1.8 2.1 2.1-1.3.3-1.8.8-2.1 2.1-.3-1.3-.8-1.8-2.1-2.1 1.3-.3 1.8-.8 2.1-2.1z" fill="currentColor" opacity="0.85" />
      <path d="M19.2 9.5c.25 1.1.7 1.55 1.8 1.8-1.1.25-1.55.7-1.8 1.8-.25-1.1-.7-1.55-1.8-1.8 1.1-.25 1.55-.7 1.8-1.8z" fill="currentColor" opacity="0.85" />
    </svg>
  );
}

export function CandyIcon({ size = 16, xl = false }: { size?: number; xl?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={{ ...iconSize(size), color: "hsl(var(--candy))" }}
      aria-hidden
    >
      <path d="M5.2 7.2 L1.6 4.6 L2.2 9 L1.6 13.4 L5.2 10.8 Z" fill="currentColor" opacity="0.8" />
      <path d="M18.8 7.2 L22.4 4.6 L21.8 9 L22.4 13.4 L18.8 10.8 Z" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="9" r="6.2" fill="currentColor" />
      <circle cx="10" cy="7.2" r="1.8" fill="hsl(var(--candy) / 0.35)" />
      {xl && (
        <text
          x="12"
          y="22.5"
          textAnchor="middle"
          fontSize="8.5"
          fontWeight="900"
          fill="currentColor"
        >
          XL
        </text>
      )}
    </svg>
  );
}

/**
 * The Mega Energy mark: our own drawing, a faceted hexagonal medallion in
 * the --mega-energy violet with a white rising double chevron (2026-09-10).
 * It replaced a bundled PNG of the game's own Mega Energy medallion, which
 * was a Scopely-owned asset (docs/ip-audit.md). `size` is the box.
 */
export function MegaEnergyIcon({ size = 16, style }: { size?: number; style?: CSSProperties }) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={{ ...iconSize(size), color: "hsl(var(--mega-energy))", ...style }}
      aria-hidden
      data-testid="mega-energy-icon"
    >
      <path d="M12 1.8l9 5.2v10.4l-9 5.2-9-5.2V7z" fill="currentColor" />
      <path d="M12 4.2l6.9 4V16l-6.9 4-6.9-4V8.2z" fill="none" stroke="#fff" strokeOpacity="0.35" strokeWidth="0.9" />
      <path
        d="M7.6 12.6L12 8.4l4.4 4.2M7.6 16.4L12 12.2l4.4 4.2"
        fill="none"
        stroke="#fff"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The cost line: one chip per owed resource. `size` picks the display scale
 * ("md" for a hero line, "sm" for list rows).
 */
export function PowerUpCostRow({
  cost,
  size = "md",
  testId,
}: {
  cost: PowerUpCost;
  size?: "md" | "sm";
  testId?: string;
}) {
  const icon = size === "md" ? 18 : 14;
  const numberClass =
    size === "md" ? "text-[15px] font-black tabular-nums" : "text-[12px] font-bold tabular-nums";
  const chips: Array<{ key: string; icon: JSX.Element; value: string; label: string }> = [];
  if (cost.dust > 0) {
    chips.push({
      key: "dust",
      icon: <StardustIcon size={icon} />,
      value: formatDust(cost.dust),
      label: `${cost.dust.toLocaleString()} stardust`,
    });
  }
  if (cost.candy > 0) {
    chips.push({
      key: "candy",
      icon: <CandyIcon size={icon} />,
      value: String(cost.candy),
      label: `${cost.candy} candy`,
    });
  }
  if (cost.candyXl > 0) {
    chips.push({
      key: "candyXl",
      icon: <CandyIcon size={icon + 2} xl />,
      value: String(cost.candyXl),
      label: `${cost.candyXl} XL candy`,
    });
  }
  if (chips.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-3" data-testid={testId}>
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1"
          role="img"
          aria-label={c.label}
          title={c.label}
        >
          {c.icon}
          <span className={numberClass} style={{ color: "var(--text-primary)" }}>
            {c.value}
          </span>
        </span>
      ))}
    </span>
  );
}
